import dayjs from 'dayjs';
import holidaysData from '../data/holidays.json';
import {
  buildActivePublicHolidaysMap,
  buildPublicHolidaysMap,
  computeModalCampaign,
  getEditableHolidayDateBounds,
  getHolidayEventsForRegion,
} from './holidaysFromJson';

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * When `useHolidayDataFile` is true, HDS is on by default; only `disabled: true` or `enabled: false` turns it off.
 * Legacy config (no file) still requires `enabled: true`.
 * @param {object|undefined} holidayHours
 * @returns {boolean} true = suppress holiday hours (no profile / modal / schedule fetch)
 */
export const isHolidayHoursSuppressedByConfig = (holidayHours) => {
  if (!holidayHours) {
    return true;
  }
  if (holidayHours.disabled === true) {
    return true;
  }
  if (holidayHours.enabled === false) {
    return true;
  }
  if (holidayHours.useHolidayDataFile) {
    return false;
  }
  return !holidayHours.enabled;
};

/**
 * Inclusive start/end for schedule fetch, holiday hours UI, and deep links. Derived from publicHolidays keys.
 * @param {object|null|undefined} profile
 * @returns {{ startDate: string, endDate: string }|null}
 */
export const getHolidayDateBoundsForProfile = (profile) => {
  const fromActiveSummaries = getEditableHolidayDateBounds(profile?.holidaySummaries);
  if (fromActiveSummaries) {
    return fromActiveSummaries;
  }
  if (profile?.hdsModalStartDate && profile?.hdsModalEndDate) {
    return { startDate: profile.hdsModalStartDate, endDate: profile.hdsModalEndDate };
  }
  const keys = profile?.publicHolidays ? Object.keys(profile.publicHolidays).sort() : [];
  if (!keys.length) {
    return null;
  }
  return { startDate: keys[0], endDate: keys[keys.length - 1] };
};

/**
 * Flatten legacy single-block holidayHours or return configured profiles.
 * @param {object|undefined} holidayHours
 * @returns {object[]}
 */
export const normalizeHolidayHours = (holidayHours) => {
  if (!holidayHours) {
    return [];
  }
  if (Array.isArray(holidayHours.profiles) && holidayHours.profiles.length > 0) {
    return holidayHours.profiles;
  }

  const {
    regions,
    onlyEditHolidayDays,
    hdsModalStartDate,
    hdsModalEndDate,
    rangeStart,
    rangeEnd,
    publicHolidays,
  } = holidayHours;

  return [
    {
      regions: regions ?? [],
      onlyEditHolidayDays,
      hdsModalStartDate,
      hdsModalEndDate,
      rangeStart,
      rangeEnd,
      publicHolidays,
    },
  ];
};

/**
 * First matching profile: empty regions = all regions; otherwise restaurant.region must be listed.
 * @param {object|undefined} restaurant
 * @param {object|undefined} holidayHours — raw block from app config (may be legacy or profiles)
 * @returns {object|null}
 */
export const getHolidayHoursProfileForRestaurant = (restaurant, holidayHours) => {
  if (isHolidayHoursSuppressedByConfig(holidayHours)) {
    return null;
  }

  const profiles = normalizeHolidayHours(holidayHours);
  const region = restaurant?.region;

  const base =
    profiles.find((profile) => {
      const list = profile.regions ?? [];
      return !list.length || (region && list.includes(region));
    }) ?? null;

  if (!base) {
    return null;
  }

  if (!holidayHours.useHolidayDataFile) {
    return base;
  }

  const events = getHolidayEventsForRegion(holidaysData, region);
  if (!events.length) {
    return null;
  }

  const publicHolidaysAll = buildPublicHolidaysMap(events);
  const leadDays = holidayHours.modalLeadDays ?? 21;
  const today = dayjs();
  const campaign = computeModalCampaign(events, today, leadDays);
  const summaries = campaign?.summaries ?? [];
  const publicHolidays = summaries.length
    ? buildActivePublicHolidaysMap(publicHolidaysAll, summaries, today)
    : publicHolidaysAll;

  return {
    ...base,
    publicHolidays,
    hdsModalStartDate: campaign?.hdsModalStartDate ?? null,
    hdsModalEndDate: campaign?.hdsModalEndDate ?? null,
    holidaySummaries: summaries,
  };
};

/**
 * Dev-time check: each profile should declare at least one public holiday date (YYYY-MM-DD).
 * @param {object|undefined} holidayHours
 * @param {string} localeKey
 */
export const assertHolidayProfilesConfig = (holidayHours, localeKey) => {
  if (holidayHours?.useHolidayDataFile) {
    return;
  }

  normalizeHolidayHours(holidayHours).forEach((profile, index) => {
    const keys = Object.keys(profile.publicHolidays ?? {});
    if (keys.length === 0) {
      const msg = `[holidayHours] ${localeKey} profile[${index}]: publicHolidays must be non-empty.`;
      if (import.meta.env.DEV) {
        console.error(msg);
      } else {
        console.warn(msg);
      }
      return;
    }
    keys.forEach((k) => {
      if (!ISO_DATE.test(k) || !dayjs(k, 'YYYY-MM-DD', true).isValid()) {
        const msg = `[holidayHours] ${localeKey} profile[${index}]: invalid publicHolidays key "${k}" (use YYYY-MM-DD).`;
        if (import.meta.env.DEV) {
          console.error(msg);
        } else {
          console.warn(msg);
        }
      }
    });
  });
};
